import { MarkerType } from '@xyflow/react';
import { DEFAULT_REASONING_EFFORT } from '@/aiAssistant/aiAssistant.types';
import type { Edge, EdgeMarker, Node } from '@xyflow/react';
import type { ChatAttachment, ChatMessage, ReasoningEffort } from '@/aiAssistant/aiAssistant.types';
import type { CustomAgentDraftInfoOutputV1 } from '@seeqdev/api/model/CustomAgentDraftInfoOutputV1';
import type { CustomAgentMetaOutputV1 } from '@seeqdev/api/model/CustomAgentMetaOutputV1';
import type { CustomAgentOutputV1 } from '@seeqdev/api/model/CustomAgentOutputV1';
import type { CustomAgentScheduleTriggerOutputV1 } from '@seeqdev/api/model/CustomAgentScheduleTriggerOutputV1';
import type { CustomAgentSummaryOutputV1 } from '@seeqdev/api/model/CustomAgentSummaryOutputV1';
import type { CustomAgentVersionOutputV1 } from '@seeqdev/api/model/CustomAgentVersionOutputV1';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';

export type AgentType = 'standard' | 'modified' | 'custom';

export type NodeStatus = 'idle' | 'pending' | 'success' | 'error' | 'canceled';

export type ContextLink = {
  type: 'Document' | 'Text';
  id?: string;
  text?: string;
  name?: string;
};

export type SeeqContextPayload = {
  workbookId?: string;
  worksheetId?: string;
  projectId?: string;
  folderId?: string;
  seeqUrl?: string;
  title?: string;
  contextNodeId?: string;
};

export type ClassificationOption = {
  id: string;
  label: string;
};

/**
 * Reference to a published Skill Builder skill attached to a workflow node.
 * Mirrors backend SkillReference in genai/app/workflows/models.py. Only the
 * reference is persisted; skill content is resolved at run time under the
 * executing user's identity.
 */
export type SkillReference = {
  skillId: string;
  name: string;
  description?: string;
};

/**
 * A built-in core skill a node preloads into its agent (e.g. `get_time_series_data`),
 * surfaced read-only alongside the node's tools. Mirrors the backend payload from
 * GET /genai/agent-builder/node-capabilities.
 */
export type NodeBuiltinSkill = {
  skillId: string;
  name: string;
  description: string;
  functions: string[];
};

/** Per-node-type capabilities: the tools it exposes plus its built-in skills. */
export type NodeCapabilities = {
  tools: string[];
  builtinSkills: NodeBuiltinSkill[];
};

export type UpstreamMemoryMode = 'all_previous_steps' | 'only_immediate_previous_step' | 'no_memory';

export type AgentNodeData = {
  title: string;
  description?: string;
  agentType: AgentType;
  preconfiguredAgentKey?: string;
  contextVisible: boolean;
  linkedContext: ContextLink[];
  seeqContext?: SeeqContextPayload;
  seeqContexts?: SeeqContextPayload[];
  skills?: SkillReference[];
  status?: NodeStatus;
  upstreamMemoryMode?: UpstreamMemoryMode;
  // No `reasoningEffort`: these nodes delegate to another agent's workflow, which carries its own.
};

export type TriggerType = 'chat' | 'schedule';

/** How the caller can reach an agent, as understood by the `ownership` query parameter. */
export type AgentBuilderOwnershipFilter = 'all' | 'mine' | 'accessible';

/**
 * Server-side filters for the published custom-agent list. Each maps to a query parameter applied
 * before the endpoint's row limit, so callers needing a narrow set must ask for it here rather than
 * filtering the response after pagination has already truncated it.
 */
export type GetAgentBuilderPublishedOptions = {
  /** Adds `productionVersionId`/`productionTriggerType`, avoiding a per-agent versions fetch. */
  includeProductionMetadata?: boolean;
  /** Restricts to agents with a published production version. */
  productionOnly?: boolean;
  /** Omitted means the endpoint default (`all`). `accessible` excludes admin-elevation-only agents. */
  ownership?: AgentBuilderOwnershipFilter;
};

export type ChatSubType = 'message' | 'input_form' | 'button';

export type InputFormFieldType = 'text' | 'multiple_choice' | 'date';

export type InputFormField = {
  id: string;
  label: string;
  description?: string;
  defaultValue?: string;
  fieldType: InputFormFieldType;
  required?: boolean;
  options?: string[]; // For multiple_choice fields
};

export type StartNodeData = {
  label: string;
  status?: NodeStatus;
  triggerType?: TriggerType;
  chatSubType?: ChatSubType;
  inputFormFields?: InputFormField[];
};

export type SeeqNodeData = {
  title: string;
  description?: string;
  seeqUrl?: string;
  workbookId?: string;
  worksheetId?: string;
  projectId?: string;
  folderId?: string;
  allowFolderContent?: boolean;
  /** Content type: 'Analysis', 'Topic', 'Vantage', or 'Project' - used for icon display */
  contentType?: string;
  status?: NodeStatus;
};

/** Publish options for create-report action nodes */
export type PublishOption = 'stream' | 'organizer' | 'save-as-file';

export type ActionNodeData = {
  title: string;
  description?: string;
  actionType: string;
  contextVisible: boolean;
  linkedContext: ContextLink[];
  seeqContext?: SeeqContextPayload;
  seeqContexts?: SeeqContextPayload[];
  skills?: SkillReference[];
  status?: NodeStatus;
  /** Publish option for create-report action type */
  publishOption?: PublishOption;
  upstreamMemoryMode?: UpstreamMemoryMode;
  reasoningEffort?: ReasoningEffort;
};

export type ClassifyNodeData = {
  title: string;
  description?: string;
  actionType: 'classify';
  classificationOptions: ClassificationOption[];
  categories?: string[];
  status?: NodeStatus;
  upstreamMemoryMode?: UpstreamMemoryMode;
  reasoningEffort?: ReasoningEffort;
};

export type NewNodeData = {
  sourceNodeId: string;
  sourceHandle?: string;
};

export type NoteNodeData = {
  content: string;
  width?: number;
  height?: number;
};

export type NotificationNodeData = {
  title: string;
  actionType: 'notification';
  subject?: string;
  recipients?: string[];
  message?: string;
  skills?: SkillReference[];
  status?: NodeStatus;
  upstreamMemoryMode?: UpstreamMemoryMode;
  reasoningEffort?: ReasoningEffort;
};

export type QuestionNodeData = {
  title: string;
  actionType: 'question';
  description: string;
  status?: NodeStatus;
  upstreamMemoryMode?: UpstreamMemoryMode;
  reasoningEffort?: ReasoningEffort;
};

export type NodeData =
  | AgentNodeData
  | StartNodeData
  | SeeqNodeData
  | ActionNodeData
  | ClassifyNodeData
  | NotificationNodeData
  | QuestionNodeData
  | NewNodeData
  | NoteNodeData;

export type EdgeType = 'workflow' | 'context' | 'classify';

export const DEFAULT_EDGE_MARKER: EdgeMarker = {
  type: MarkerType.ArrowClosed,
  width: 20,
  height: 20,
  // Use user space units so marker size stays consistent without explicit sizing
  markerUnits: 'userSpaceOnUse',
};

export const CONTEXT_EDGE_MARKER: EdgeMarker = {
  type: MarkerType.ArrowClosed,
  width: 20,
  height: 20,
  markerUnits: 'userSpaceOnUse',
  color: '#b19cd9', // Same purple as the edge stroke
};

/** Shared styling constants for context edges and connection lines */
export const CONTEXT_EDGE_STYLE = {
  color: '#b19cd9',
  strokeDasharray: '5, 5',
  strokeWidth: 2,
  strokeWidthSelected: 3,
} as const;

/**
 * Maps the leading token of a tool name (e.g. `files_read` -> `files`) to a
 * Font Awesome icon present in `app/resources/fa-icons.json`. Verb prefixes
 * are split by intent: `get` is read-shaped, the remaining verbs share a
 * generic edit icon since they don't carry domain meaning on their own.
 */
export const TOOL_PREFIX_ICONS: Record<string, string> = {
  files: 'fa-file',
  content: 'fa-file-lines',
  data: 'fa-database',
  code: 'fa-code',
  datalab: 'fa-folder-open',
  knowledge: 'fa-book',
  company: 'fa-book',
  documents: 'fa-files',
  search: 'fa-magnifying-glass',
  visualize: 'fa-chart-line',
  think: 'fa-eye',
  respond: 'fa-reply',
  create: 'fa-pencil',
  update: 'fa-pencil',
  load: 'fa-pencil',
  set: 'fa-pencil',
  add: 'fa-pencil',
  get: 'fa-list',
};

/** Fallback icon used by `getToolIcon` when no prefix matches. */
export const FALLBACK_TOOL_ICON = 'fa-wrench';

export type AgentBuilderEdgeData = {
  edgeType: EdgeType;
  /** Label for classify edges - the category name */
  categoryLabel?: string;
  /** Index of the category in the classification options (for color) */
  categoryIndex?: number;
};

export type AgentBuilderNode = Node<NodeData>;
export type AgentBuilderEdge = Edge<AgentBuilderEdgeData>;

export type AgentBuilderMeta = {
  createdAt?: string;
  updatedAt?: string;
  hasDraft?: boolean;
  draftBaseVersionId?: string | null;
  draftUpdatedAt?: string;
};

export type AgentBuilderGraph = {
  id: string;
  name: string;
  icon?: string;
  iconColor?: string;
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
  meta: AgentBuilderMeta;
  effectivePermissions?: PermissionsV1;
  isOwner?: boolean;
};

export type AgentBuilderScheduleTrigger = Omit<CustomAgentScheduleTriggerOutputV1, 'effectiveEnabled'> & {
  effectiveEnabled?: boolean;
};

/**
 * A single entry in the Agent Builder undo/redo history stacks.
 * Captures just the workflow (nodes/edges) at the time of recording.
 * History is intentionally scoped to draft-only workflow changes; title
 * edits and other metadata are not tracked.
 */
export type AgentBuilderHistoryEntry = {
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
  recordedAt: number;
};

/**
 * The full undo/redo history payload held in memory. A stack of past entries
 * and a stack of future entries that can be reapplied via redo. The persistence
 * layer transforms this into a diff-based representation before writing to
 * localStorage (see `PersistedEditorHistory`).
 */
export type AgentBuilderHistoryPayload = {
  past: AgentBuilderHistoryEntry[];
  future: AgentBuilderHistoryEntry[];
};

/**
 * A workflow snapshot: just the nodes and edges, no surrounding metadata. The
 * unit the persistence layer diffs and hashes against.
 */
export type WorkflowSnapshot = {
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
};

/**
 * A single forward-direction diff in the persisted chain. `patch` transforms
 * the predecessor state into this entry's state; `resultHash` is the hash of
 * this entry's state (used to validate chain integrity on rehydrate).
 */
export type PersistedDiff = {
  patch: Record<string, unknown>;
  resultHash: string;
  recordedAt: number;
};

/**
 * The localStorage payload shape introduced in the diff-based persistence
 * design. Ordered oldest → newest, capped at 10 diffs. The chain is anchored
 * implicitly at the newest end: the last diff's `resultHash` must equal the
 * hash of the current canvas state on rehydrate.
 *
 * Cleared on revert/publish/delete; reaped after 30 days of inactivity.
 */
export type PersistedEditorHistory = {
  lastWrittenAt: number;
  diffs: PersistedDiff[];
};

/**
 * Represents a request to trigger an agent builder from an external source (e.g., AgentBuilderChatModal).
 * When set in the AI Assistant store, the AI Assistant will:
 * 1. Switch to agent builder mode with the specified agent builder
 * 2. Automatically submit the prompt to start the agent builder execution
 */
export type ExternalAgentBuilderPrompt = {
  /** The agent builder graph to execute */
  agentBuilder: AgentBuilderGraph;
  /** The user's initial prompt/message to send to the agent builder */
  prompt: string;
  /** Optional attachments to include with the prompt */
  attachments?: ChatAttachment[];
  /** Version label for display - 'Draft', 'Production', or version number like 'v3' */
  versionLabel?: string;
  /** Selected version ID from the Agent Builder header, null = draft, string = specific version */
  selectedVersionId?: string | null;
  /** Input form fields for input_form chat sub-type triggers */
  inputFormFields?: InputFormField[];
  /** Versions the caller already fetched, so the assistant can skip its own versions request. */
  versions?: AgentBuilderVersion[];
  /** When true, allows submitting an empty prompt to start a run (used by button triggers) */
  allowEmptyPrompt?: boolean;
  /** When true, the agent builder is a built-in template (skip draft save, use templateId for runs) */
  isTemplate?: boolean;
};

export type GetAgentBuilderOptions = {
  /** If true, fetches the production version instead of current state */
  production?: boolean;
  /** Fetch a specific version's graph */
  versionId?: string;
};

export type AgentBuilderListItem = {
  id: string;
  name: string;
  icon?: string;
  iconColor?: string;
  updatedAt?: string;
  createdAt?: string;
  hasDraft?: boolean;
  draftBaseVersionId?: string | null;
  draftUpdatedAt?: string;
  effectivePermissions?: PermissionsV1;
  isOwner?: boolean;
  // True when the current user has a real ACL entry on the agent (themselves or via group
  // membership), independent of admin elevation. Used by the dashboard's "Shared" filter so
  // admins can distinguish items truly shared with them from items they only see via admin
  // override.
  isSharedWithCurrentUser?: boolean;
  creatorName?: string;
  productionVersionId?: string | null;
  /** Start-node trigger of the production version. Null when unpublished or unrecognized. */
  productionTriggerType?: TriggerType | null;
};

export type AgentBuilderTemplateListItem = {
  id: string;
  title: string;
  description: string;
  icon?: string;
  iconColor?: string;
  isChatRunnable?: boolean;
};

export type AgentBuilderVersion = {
  versionId: string;
  createdAt: string;
  createdBy?: string;
  production?: boolean;
};

export const TEMPLATE_CARDS = [
  {
    id: 'data-enrichment',
    title: 'Data enrichment',
    description: 'Pull together data to answer user questions',
  },
  {
    id: 'planning-helper',
    title: 'Planning helper',
    description: 'Simple multi-turn workflow for creating work plans',
  },
  {
    id: 'customer-service',
    title: 'Customer service',
    description: 'Resolve customer queries with custom policies',
  },
  {
    id: 'structured-data-qa',
    title: 'Structured Data Q/A',
    description: 'Query databases using natural language',
  },
  {
    id: 'document-comparison',
    title: 'Document comparison',
    description: 'Analyze and highlight differences across uploaded documents',
  },
  {
    id: 'internal-knowledge',
    title: 'Internal knowledge assistant',
    description: 'Triage and answer questions from employees',
  },
];

export const TAB_OPTIONS = ['CUSTOM_AGENTS', 'Drafts', 'Templates', 'Runs'] as const;
export type TabType = (typeof TAB_OPTIONS)[number];

export type BuildSeeqContextArgs = {
  targetNodeId: string;
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
  newPosition?: { x: number; y: number };
};

export type BuildSeeqContextResult = {
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
  newNodeId?: string;
};

// ============================================================================
// Node Definition Registry - Single Source of Truth
// ============================================================================

/** Palette category for grouping nodes in the UI */
export type PaletteCategory = 'flows' | 'read' | 'create' | 'edit';

/**
 * Complete node definition including all metadata.
 * This is the single source of truth for all node types in the agent builder.
 */
export type NodeDefinition = {
  /** Unique node type identifier */
  type: string;
  /** Icon class for the node */
  icon: string;
  /** Translation key for the node label */
  labelKey: string;
  /** Translation key for the node description */
  descriptionKey: string;
  /** Palette category for grouping in the UI */
  paletteCategory: PaletteCategory;
  /** Whether this node is UI-only (not sent to backend workflow runner) */
  uiOnly: boolean;
  /** Whether this node supports Seeq context connections */
  supportsContext: boolean;
  /** Whether this node appears in the palette (some nodes like 'start' don't) */
  showInPalette: boolean;
};

/**
 * All node definitions in the agent builder.
 * To add a new node type, add it here - this is the only place you need to update.
 */
export const NODE_DEFINITIONS: NodeDefinition[] = [
  // UI-only nodes (not sent to backend)
  {
    type: 'start',
    icon: 'fa-play',
    labelKey: 'AGENT_BUILDER.START',
    descriptionKey: 'AGENT_BUILDER.START_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: true,
    supportsContext: false,
    showInPalette: false, // Start node is auto-created, not added from palette
  },
  {
    type: 'seeq',
    icon: 'fc-seeq-content',
    labelKey: 'AGENT_BUILDER.SEEQ',
    descriptionKey: 'AGENT_BUILDER.SEEQ_DESCRIPTION',
    paletteCategory: 'read',
    uiOnly: true,
    supportsContext: false,
    showInPalette: true,
  },
  {
    type: 'note',
    icon: 'fa-file',
    labelKey: 'AGENT_BUILDER.NOTE',
    descriptionKey: 'AGENT_BUILDER.NOTE_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: true,
    supportsContext: false,
    showInPalette: true,
  },
  // Flow control nodes (sent to backend)
  {
    type: 'classify',
    icon: 'fa-list-check',
    labelKey: 'AGENT_BUILDER.CLASSIFY',
    descriptionKey: 'AGENT_BUILDER.CLASSIFY_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
  // Search & Read nodes - for discovering and retrieving content
  {
    type: 'search-seeq',
    icon: 'fa-magnifying-glass',
    labelKey: 'AGENT_BUILDER.SEARCH_SEEQ',
    descriptionKey: 'AGENT_BUILDER.SEARCH_SEEQ_DESCRIPTION',
    paletteCategory: 'read',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  // Read nodes - for reading and retrieving existing content
  {
    type: 'read-corporate-knowledge',
    icon: 'fa-circle-info',
    labelKey: 'AGENT_BUILDER.READ_CORPORATE_KNOWLEDGE',
    descriptionKey: 'AGENT_BUILDER.READ_CORPORATE_KNOWLEDGE_DESCRIPTION',
    paletteCategory: 'read',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
  {
    type: 'read-project-files',
    icon: 'fa-folder-open',
    labelKey: 'AGENT_BUILDER.READ_PROJECT_FILES',
    descriptionKey: 'AGENT_BUILDER.READ_PROJECT_FILES_DESCRIPTION',
    paletteCategory: 'read',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  // Create nodes - for creating new content
  {
    type: 'create-seeq-content',
    icon: 'fa-circle-plus',
    labelKey: 'AGENT_BUILDER.CREATE_SEEQ_CONTENT',
    descriptionKey: 'AGENT_BUILDER.CREATE_SEEQ_CONTENT_DESCRIPTION',
    paletteCategory: 'create',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'create-report',
    icon: 'fa-file',
    labelKey: 'AGENT_BUILDER.CREATE_REPORT',
    descriptionKey: 'AGENT_BUILDER.CREATE_REPORT_DESCRIPTION',
    paletteCategory: 'create',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'create-item',
    icon: 'fc-formula',
    labelKey: 'AGENT_BUILDER.CREATE_ITEM',
    descriptionKey: 'AGENT_BUILDER.CREATE_ITEM_DESCRIPTION',
    paletteCategory: 'create',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  // Edit nodes - for modifying existing content
  {
    type: 'write-organizer',
    icon: 'fa-pencil',
    labelKey: 'AGENT_BUILDER.WRITE_ORGANIZER',
    descriptionKey: 'AGENT_BUILDER.WRITE_ORGANIZER_DESCRIPTION',
    paletteCategory: 'edit',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'write-journal',
    icon: 'fa-book',
    labelKey: 'AGENT_BUILDER.WRITE_JOURNAL',
    descriptionKey: 'AGENT_BUILDER.WRITE_JOURNAL_DESCRIPTION',
    paletteCategory: 'edit',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'add-vantage-comments',
    icon: 'fa-comment-dots',
    labelKey: 'AGENT_BUILDER.ADD_VANTAGE_CONTEXT',
    descriptionKey: 'AGENT_BUILDER.ADD_VANTAGE_CONTEXT_DESCRIPTION',
    paletteCategory: 'edit',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'write-project-files',
    icon: 'fa-upload',
    labelKey: 'AGENT_BUILDER.WRITE_PROJECT_FILES',
    descriptionKey: 'AGENT_BUILDER.WRITE_PROJECT_FILES_DESCRIPTION',
    paletteCategory: 'edit',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'load-item',
    icon: 'fa-upload',
    labelKey: 'AGENT_BUILDER.LOAD_ITEM',
    descriptionKey: 'AGENT_BUILDER.LOAD_ITEM_DESCRIPTION',
    paletteCategory: 'edit',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  // Core nodes - for analysis and response
  {
    type: 'analyze-data',
    icon: 'fa-chart-line',
    labelKey: 'AGENT_BUILDER.ANALYZE_DATA',
    descriptionKey: 'AGENT_BUILDER.ANALYZE_DATA_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: true,
    showInPalette: true,
  },
  {
    type: 'think',
    icon: 'fa-brain-circuit',
    labelKey: 'AGENT_BUILDER.THINK',
    descriptionKey: 'AGENT_BUILDER.THINK_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
  {
    type: 'respond',
    icon: 'fa-reply',
    labelKey: 'AGENT_BUILDER.RESPOND',
    descriptionKey: 'AGENT_BUILDER.RESPOND_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
  {
    type: 'notification',
    icon: 'fc-bell-regular',
    labelKey: 'AGENT_BUILDER.NOTIFICATION',
    descriptionKey: 'AGENT_BUILDER.NOTIFICATION_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
  {
    type: 'run-agent',
    icon: 'fa-sparkles',
    labelKey: 'AGENT_BUILDER.RUN_AGENT',
    descriptionKey: 'AGENT_BUILDER.RUN_AGENT_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
  {
    type: 'question',
    icon: 'fa-circle-question',
    labelKey: 'AGENT_BUILDER.QUESTION',
    descriptionKey: 'AGENT_BUILDER.QUESTION_DESCRIPTION',
    paletteCategory: 'flows',
    uiOnly: false,
    supportsContext: false,
    showInPalette: true,
  },
];

// ============================================================================
// Derived Constants and Helper Functions (computed from NODE_DEFINITIONS)
// ============================================================================

/** Map of node type to definition for O(1) lookup */
const NODE_DEFINITION_MAP = new Map<string, NodeDefinition>(NODE_DEFINITIONS.map((def) => [def.type, def]));

/** Get the full definition for a node type */
export function getNodeDefinition(type: string): NodeDefinition | undefined {
  return NODE_DEFINITION_MAP.get(type);
}

/** All action node types (non-UI-only nodes that are sent to backend) */
export const ACTION_NODE_TYPES = NODE_DEFINITIONS.filter((def) => !def.uiOnly).map(
  (def) => def.type,
) as readonly string[];

/** Type representing any action node type */
export type ActionNodeType = (typeof ACTION_NODE_TYPES)[number];

/** UI-only node types that are not sent to the backend workflow runner */
export const UI_ONLY_NODE_TYPES = NODE_DEFINITIONS.filter((def) => def.uiOnly).map(
  (def) => def.type,
) as readonly string[];

/** Type representing UI-only node types */
export type UIOnlyNodeType = (typeof UI_ONLY_NODE_TYPES)[number];

/** Helper function to check if a node type is an action node */
export function isActionNodeType(type: string | undefined): type is ActionNodeType {
  if (type === undefined) return false;
  const def = NODE_DEFINITION_MAP.get(type);
  return def !== undefined && !def.uiOnly;
}

/** Helper function to check if a node type is UI-only */
export function isUIOnlyNodeType(type: string | undefined): type is UIOnlyNodeType {
  if (type === undefined) return false;
  const def = NODE_DEFINITION_MAP.get(type);
  return def !== undefined && def.uiOnly;
}

/** Action types that support Seeq context connections */
export const ACTION_TYPES_WITH_CONTEXT = NODE_DEFINITIONS.filter((def) => def.supportsContext).map(
  (def) => def.type,
) as readonly string[];

export const THINK_NODE_TYPE = 'think';
export const READ_CORPORATE_KNOWLEDGE_NODE_TYPE = 'read-corporate-knowledge';

/** Effort used when node data has none stored. Must match the backend node adapters' fallbacks. */
export function getDefaultNodeReasoningEffort(nodeType: string | undefined): ReasoningEffort {
  if (nodeType === THINK_NODE_TYPE) {
    return 'high';
  }
  if (nodeType === READ_CORPORATE_KNOWLEDGE_NODE_TYPE) {
    return 'medium';
  }
  return DEFAULT_REASONING_EFFORT;
}

export function getNodeStatusClass(status: NodeStatus): string {
  if (status === 'pending') return 'agent-builder-status-pending';
  if (status === 'success') return 'agent-builder-status-success';
  if (status === 'error') return 'agent-builder-status-error';
  if (status === 'canceled') return 'agent-builder-status-canceled';
  return '';
}

/** Get icon for any node type */
export function getIconForNodeType(nodeType: string): string {
  return NODE_DEFINITION_MAP.get(nodeType)?.icon || 'fa-bolt';
}

/**
 * Label key overrides for action-type display titles.
 *
 * Titles normally derive their i18n key from the action type (`ACTION_TYPES.<TYPE>`). When a node is
 * renamed in the UI but keeps its persisted action type id, we point at a different key instead of
 * repurposing the original `ACTION_TYPES.<TYPE>` value. Translations are served centrally, so editing
 * an existing value in place would change the label for older app versions still resolving that key.
 */
const ACTION_TYPE_LABEL_KEY_OVERRIDES: Record<string, string> = {
  'add-vantage-comments': 'AGENT_BUILDER.ADD_VANTAGE_CONTEXT',
};

/** i18n key for an action type's display title. */
export function getActionTypeLabelKey(actionType: string): string {
  return (
    ACTION_TYPE_LABEL_KEY_OVERRIDES[actionType] ??
    `AGENT_BUILDER.ACTION_TYPES.${actionType.toUpperCase().replace(/-/g, '_')}`
  );
}

// ============================================================================
// Palette Definitions (derived from NODE_DEFINITIONS)
// ============================================================================

export type NodePaletteDefinition = {
  type: string;
  category: PaletteCategory;
  labelKey: string;
  descriptionKey: string;
  icon: string;
};

/** Palette definitions derived from NODE_DEFINITIONS */
export const NODE_PALETTE_DEFINITIONS: NodePaletteDefinition[] = NODE_DEFINITIONS.filter(
  (def) => def.showInPalette,
).map((def) => ({
  type: def.type,
  category: def.paletteCategory,
  labelKey: def.labelKey,
  descriptionKey: def.descriptionKey,
  icon: def.icon,
}));

export type NodePaletteItem = {
  type: string;
  label: string;
  icon: string;
  description: string;
  category: PaletteCategory;
};

// ============================================================================
// Inspector Tips Configuration
// ============================================================================

export type InspectorTipType =
  | 'action'
  | 'agent'
  | 'classify'
  | 'seeq'
  | 'start'
  | 'startInputForm'
  | 'startSchedule'
  | 'startTime'
  | 'startCondition'
  | 'searchSeeq'
  | 'readCorporateKnowledge'
  | 'analyzeData'
  | 'addVantageComments'
  | 'readProjectFiles'
  | 'createSeeqContent'
  | 'writeOrganizer'
  | 'writeJournal'
  | 'createReport'
  | 'think'
  | 'createItem'
  | 'loadItem'
  | 'writeProjectFiles'
  | 'respond'
  | 'notification'
  | 'question'
  | 'runAgent';

export const INSPECTOR_TIPS_CONFIG: Record<InspectorTipType, string[]> = {
  action: ['AGENT_BUILDER.TIPS.ACTION.TIP_1', 'AGENT_BUILDER.TIPS.ACTION.TIP_2', 'AGENT_BUILDER.TIPS.ACTION.TIP_3'],
  agent: ['AGENT_BUILDER.TIPS.AGENT.TIP_1', 'AGENT_BUILDER.TIPS.AGENT.TIP_2', 'AGENT_BUILDER.TIPS.AGENT.TIP_3'],
  classify: [
    'AGENT_BUILDER.TIPS.CLASSIFY.TIP_1',
    'AGENT_BUILDER.TIPS.CLASSIFY.TIP_2',
    'AGENT_BUILDER.TIPS.CLASSIFY.TIP_3',
  ],
  seeq: ['AGENT_BUILDER.TIPS.SEEQ.TIP_1', 'AGENT_BUILDER.TIPS.SEEQ.TIP_2', 'AGENT_BUILDER.TIPS.SEEQ.TIP_3'],
  start: ['AGENT_BUILDER.TIPS.START.TIP_1', 'AGENT_BUILDER.TIPS.START.TIP_2'],
  startInputForm: ['AGENT_BUILDER.TIPS.START_INPUT_FORM.TIP_1', 'AGENT_BUILDER.TIPS.START_INPUT_FORM.TIP_2'],
  startSchedule: [
    'AGENT_BUILDER.TIPS.START_SCHEDULE.TIP_1',
    'AGENT_BUILDER.TIPS.START_SCHEDULE.TIP_2',
    'AGENT_BUILDER.TIPS.START_SCHEDULE.TIP_3',
  ],
  startTime: ['AGENT_BUILDER.TIPS.START_TIME.TIP_1', 'AGENT_BUILDER.TIPS.START_TIME.TIP_2'],
  startCondition: ['AGENT_BUILDER.TIPS.START_CONDITION.TIP_1', 'AGENT_BUILDER.TIPS.START_CONDITION.TIP_2'],
  searchSeeq: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.SEARCH_SEEQ.TIP_1',
    'AGENT_BUILDER.TIPS.SEARCH_SEEQ.TIP_2',
    'AGENT_BUILDER.TIPS.SEARCH_SEEQ.TIP_3',
  ],
  readCorporateKnowledge: [
    'AGENT_BUILDER.TIPS.READ_CORPORATE_KNOWLEDGE.TIP_1',
    'AGENT_BUILDER.TIPS.READ_CORPORATE_KNOWLEDGE.TIP_2',
    'AGENT_BUILDER.TIPS.READ_CORPORATE_KNOWLEDGE.TIP_3',
  ],
  analyzeData: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.ANALYZE_DATA.TIP_1',
    'AGENT_BUILDER.TIPS.ANALYZE_DATA.TIP_2',
    'AGENT_BUILDER.TIPS.ANALYZE_DATA.TIP_3',
  ],
  addVantageComments: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.ADD_VANTAGE_CONTEXT.TIP_1',
    'AGENT_BUILDER.TIPS.ADD_VANTAGE_CONTEXT.TIP_2',
    'AGENT_BUILDER.TIPS.ADD_VANTAGE_CONTEXT.TIP_3',
  ],
  readProjectFiles: [
    'AGENT_BUILDER.TIPS.READ_PROJECT_FILES.TIP_1',
    'AGENT_BUILDER.TIPS.READ_PROJECT_FILES.TIP_2',
    'AGENT_BUILDER.TIPS.READ_PROJECT_FILES.TIP_3',
    'AGENT_BUILDER.TIPS.READ_PROJECT_FILES.TIP_4',
  ],
  createSeeqContent: [
    'AGENT_BUILDER.TIPS.CREATE_SEEQ_CONTENT.TIP_1',
    'AGENT_BUILDER.TIPS.CREATE_SEEQ_CONTENT.TIP_2',
    'AGENT_BUILDER.TIPS.CREATE_SEEQ_CONTENT.TIP_3',
  ],
  writeOrganizer: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.WRITE_ORGANIZER.TIP_1',
    'AGENT_BUILDER.TIPS.WRITE_ORGANIZER.TIP_2',
    'AGENT_BUILDER.TIPS.WRITE_ORGANIZER.TIP_3',
  ],
  writeJournal: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.WRITE_JOURNAL.TIP_1',
    'AGENT_BUILDER.TIPS.WRITE_JOURNAL.TIP_2',
    'AGENT_BUILDER.TIPS.WRITE_JOURNAL.TIP_3',
  ],
  createReport: [
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_1',
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_2',
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_3',
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_4',
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_5',
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_6',
    'AGENT_BUILDER.TIPS.CREATE_REPORT.TIP_7',
  ],
  think: ['AGENT_BUILDER.TIPS.THINK.TIP_1', 'AGENT_BUILDER.TIPS.THINK.TIP_2', 'AGENT_BUILDER.TIPS.THINK.TIP_3'],
  createItem: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.CREATE_ITEM.TIP_1',
    'AGENT_BUILDER.TIPS.CREATE_ITEM.TIP_2',
    'AGENT_BUILDER.TIPS.CREATE_ITEM.TIP_3',
  ],
  writeProjectFiles: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.WRITE_PROJECT_FILES.TIP_1',
    'AGENT_BUILDER.TIPS.WRITE_PROJECT_FILES.TIP_2',
    'AGENT_BUILDER.TIPS.WRITE_PROJECT_FILES.TIP_3',
  ],
  loadItem: [
    'AGENT_BUILDER.TIPS.CODE_SUPPORTED',
    'AGENT_BUILDER.TIPS.LOAD_ITEM.TIP_1',
    'AGENT_BUILDER.TIPS.LOAD_ITEM.TIP_2',
    'AGENT_BUILDER.TIPS.LOAD_ITEM.TIP_3',
  ],
  respond: ['AGENT_BUILDER.TIPS.RESPOND.TIP_1', 'AGENT_BUILDER.TIPS.RESPOND.TIP_2', 'AGENT_BUILDER.TIPS.RESPOND.TIP_3'],
  notification: ['AGENT_BUILDER.TIPS.NOTIFICATION.TIP_1', 'AGENT_BUILDER.TIPS.NOTIFICATION.TIP_2'],
  question: [
    'AGENT_BUILDER.TIPS.QUESTION.TIP_1',
    'AGENT_BUILDER.TIPS.QUESTION.TIP_2',
    'AGENT_BUILDER.TIPS.QUESTION.TIP_3',
  ],
  runAgent: [
    'AGENT_BUILDER.TIPS.RUN_AGENT.TIP_1',
    'AGENT_BUILDER.TIPS.RUN_AGENT.TIP_2',
    'AGENT_BUILDER.TIPS.RUN_AGENT.TIP_3',
  ],
};

export function getInspectorTips(tipType: InspectorTipType): string[] {
  return INSPECTOR_TIPS_CONFIG[tipType] || [];
}

/**
 * Converts an action node type to its corresponding InspectorTipType.
 * Falls back to 'action' for unknown types.
 */
export function getTipTypeForActionType(actionType: string): InspectorTipType {
  const tipTypeMap: Record<string, InspectorTipType> = {
    'search-seeq': 'searchSeeq',
    'search-seeq-content': 'searchSeeq',
    'search-seeq-items': 'searchSeeq',
    'read-seeq-content': 'searchSeeq',
    'read-corporate-knowledge': 'readCorporateKnowledge',
    'analyze-data': 'analyzeData',
    'add-vantage-comments': 'addVantageComments',
    'read-project-files': 'readProjectFiles',
    'create-seeq-content': 'createSeeqContent',
    'write-organizer': 'writeOrganizer',
    'write-journal': 'writeJournal',
    'create-report': 'createReport',
    'think': 'think',
    'create-item': 'createItem',
    'load-item': 'loadItem',
    'write-project-files': 'writeProjectFiles',
    'respond': 'respond',
    'notification': 'notification',
    'run-agent': 'runAgent',
  };
  return tipTypeMap[actionType] || 'action';
}

// ============================================================================
// Agent Builder Run Types (aligned with backend models in genai/app/workflows/models.py)
// ============================================================================

export type AgentBuilderRunAgentNames = ReadonlyMap<string, string>;

export type AgentBuilderUserLabels = Readonly<Partial<Record<string, string>>>;

export const AGENT_BUILDER_RUN_STATES = ['queued', 'running', 'finished', 'error', 'canceled'] as const;
export type AgentBuilderRunState = (typeof AGENT_BUILDER_RUN_STATES)[number];

export type AgentBuilderOrigin =
  | 'workbench'
  | 'organizer'
  | 'vantage'
  | 'datalab'
  | 'homescreen'
  | 'chat'
  | 'skillsbuilder'
  | 'tree_builder';

export type AgentBuilderNodeOutput = {
  text: string;
};

export type AgentBuilderNodeSummary = {
  id: string;
  type?: string;
  label?: string;
  status?: AgentBuilderRunState;
};

export type AgentBuilderGraphSummary = {
  nodes?: AgentBuilderNodeSummary[];
  edges?: Array<{ id: string; source: string; target: string }>;
};

export type AgentBuilderRunError = {
  message: string;
  nodeId?: string;
  details?: string;
};

export type AgentBuilderContextSeed = {
  identityPath: string;
  workbookId?: string;
  worksheetId?: string;
  folderId?: string;
  authToken?: string;
  csrfToken?: string;
  requestId?: string;
  origin?: AgentBuilderOrigin;
};

export type AgentBuilderRunStartOptions = {
  stream?: boolean;
  streamTools?: boolean;
  chatId?: string;
  origin?: AgentBuilderOrigin;
};

export type AgentBuilderGraphMeta = Record<string, unknown> & {
  entryNodeIds?: string[];
};

/**
 * Graph snapshot sent to the backend to start an agent builder run.
 * Mirrors backend GraphSnapshot model.
 */
export type AgentBuilderGraphSnapshot = {
  id: string;
  name: string;
  nodes: Array<{
    id: string;
    type?: string;
    position: { x: number; y: number };
    data: Record<string, unknown>;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
    type?: string;
    data?: Record<string, unknown>;
  }>;
  versionId?: string;
  meta?: AgentBuilderGraphMeta;
};

/**
 * Payload for POST /agent-workflows/runs
 */
export type AgentBuilderRunStartPayload = {
  prompt?: string;
  chatId?: string;
  messageId?: string;
  previousMessages?: ChatMessage[];
  attachments?: ChatAttachment[];
  customAgentId?: string;
  templateId?: string;
  versionId?: string;
  inputs?: Record<string, string>;
  options: AgentBuilderRunStartOptions;
  contextSeed?: AgentBuilderContextSeed;
  /** Display label for the agent builder version used in this run (e.g. "v3").
   *  Persisted on chat messages so the UI can show which version produced each response. */
  versionLabel?: string;
};

/**
 * Response from GET /agent-workflows/runs/{runId}
 */
export type AgentBuilderRunStatus = {
  runId: string;
  state: AgentBuilderRunState;
  startedAt: string;
  updatedAt: string;
  finishedAt?: string;
  graphSummary: AgentBuilderGraphSummary;
  error?: AgentBuilderRunError;
  nodeOutputs: Record<string, AgentBuilderNodeOutput>;
  thinking?: Array<Record<string, unknown>>;
};

/** What kicked a run off: a chat message or a schedule firing. */
export const AGENT_BUILDER_RUN_TRIGGER_TYPES = ['chat', 'schedule'] as const;
export type AgentBuilderRunTriggerType = (typeof AGENT_BUILDER_RUN_TRIGGER_TYPES)[number];

/**
 * One row of GET /agent-workflows/runs. `durationMs` and `totalTokens` are absent until the run finishes,
 * since the backend derives them when it writes the terminal record; `chatId` is absent on runs recorded
 * before chats were linked to runs.
 *
 * The API reports agents and users as raw ids only. Display names are resolved client-side against the
 * agents and users the caller can read, since a run may reference either that the caller cannot see.
 */
export type AgentBuilderRunListItem = {
  runId: string;
  chatId?: string | null;
  customAgentId?: string | null;
  versionId?: string | null;
  state: AgentBuilderRunState;
  triggerType?: AgentBuilderRunTriggerType | null;
  origin?: AgentBuilderOrigin | null;
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  totalTokens?: number | null;
  /** Identity the run executed as. */
  runUserId?: string | null;
  /**
   * Identity that asked for the run. Today the backend records the same id in both fields for every trigger
   * type; the fields are kept separate so future scheduled or system triggers can report a distinct requester.
   */
  requestedByUserId?: string | null;
};

/**
 * Response from GET /agent-workflows/runs.
 */
export type AgentBuilderRunListResponse = {
  items: AgentBuilderRunListItem[];
  totalResults: number;
  visibilityDegraded?: boolean;
};

/**
 * Query parameters for GET /agent-workflows/runs. List-valued parameters are serialized as comma-separated
 * values; an empty list means "no constraint" and is omitted.
 */
export type AgentBuilderRunListParams = {
  offset?: number;
  limit?: number;
  sortField?: string;
  sortAsc?: boolean;
  states?: readonly AgentBuilderRunState[];
  triggerTypes?: readonly AgentBuilderRunTriggerType[];
  /** The only way to narrow by agent; the API has no name search, since it does not know agent names. */
  agentIds?: readonly string[];
  /**
   * The only way to narrow by user; as with agents, the API knows users by id alone. A run matches when
   * either the user it ran as or the user who requested it is in the list.
   */
  runUserIds?: readonly string[];
  startedAfter?: string;
  startedBefore?: string;
};

export interface AgentBuilderRunListRequestParams extends AgentBuilderRunListParams {
  /**
   * True when a set filter is active with no values selected, which can only match nothing. The API has no
   * parameter for that, so the caller must resolve an empty result without a request instead of sending these
   * parameters, which would otherwise return everything.
   */
  matchesNothing?: boolean;
}

/**
 * Entry for the run panel log view, derived from backend status updates.
 */
export type AgentBuilderRunLogEntry = {
  id: string;
  nodeId: string;
  nodeType: string;
  status: AgentBuilderRunState;
  message: string;
  timestamp: number;
};

/**
 * Structured error detail returned by the backend when a run start fails validation.
 */
export type AgentBuilderRunStartErrorDetail = {
  code?: string;
  message?: string;
  nodeIds?: string[];
};

/**
 * Error thrown when starting a workflow run fails with a structured backend error.
 */
export class AgentBuilderRunStartError extends Error {
  code?: string;
  nodeIds?: string[];
  status?: number;

  constructor(message: string, status?: number, code?: string, nodeIds?: string[]) {
    super(message);
    this.name = 'AgentBuilderRunStartError';
    this.status = status;
    this.code = code;
    this.nodeIds = nodeIds;
  }
}

// ============================================================================
// Core Custom Agent API Types (server response shapes)
// ============================================================================

export type CoreCustomAgentDraft = CustomAgentDraftInfoOutputV1;

export type CoreCustomAgentMeta = Omit<CustomAgentMetaOutputV1, 'draft'> & {
  draft?: CoreCustomAgentDraft | null;
};

export type CoreCustomAgentOutput = Omit<CustomAgentOutputV1, 'owner' | 'icon' | 'iconColor' | 'meta'> & {
  icon?: string | null;
  iconColor?: string | null;
  meta: CoreCustomAgentMeta;
  isOwner?: boolean;
};

export type CoreCustomAgentSummary = Omit<
  CustomAgentSummaryOutputV1,
  'owner' | 'sharedWithCurrentUser' | 'icon' | 'iconColor' | 'draft'
> & {
  icon?: string | null;
  iconColor?: string | null;
  draft?: CoreCustomAgentDraft | null;
  isOwner?: boolean;
  isSharedWithCurrentUser?: boolean;
  productionVersionId?: string | null;
  /** Unvalidated wire value; narrowed to {@link TriggerType} when mapped to an AgentBuilderListItem. */
  productionTriggerType?: string | null;
};

export type CoreCustomAgentVersion = CustomAgentVersionOutputV1;
